/*

& "Send for review?" before saving an edit to a listing (3 Nov 2026, gap A3), the app's prompt on its listing screen:
& every section save on the website unlists the listing until it is approved again (the edit routes set
& published: false and status "edited"), and the owner was never told.

  One dialog, mounted once on the listing page (<SendForReviewConfirmHost />), asked by each section's save with
  `if (!(await confirmSendForReview())) return;`. Photo edits don't ask, as in the app. Where no host is mounted (a
  section's editor reused elsewhere), it answers yes at once, so nothing else changes.

*/

import { Dialog, DialogContent, Typography } from "@mui/material";
import { useRouter } from "next/router";
import React from "react";

import BeegruButton from "../../common-components/buttons/BeegruButton";

/* The mounted host's opener, or null when none is mounted. */
let openHost: ((resolve: (confirmed: boolean) => void) => void) | null = null;

/* Set by "Discard changes?" → Save changes, whose dialog already said the save sends it for review. */
let skipNext: boolean = false;

/** The next save goes ahead without asking (its caller already showed the review note). */
export function skipNextSendForReviewConfirm(): void {
	skipNext = true;
}

/** Asks "Send for review?"; resolves true to save, false to keep editing. */
export function confirmSendForReview(): Promise<boolean> {
	if (skipNext) {
		skipNext = false;
		return Promise.resolve(true);
	}
	if (!openHost) return Promise.resolve(true);
	const opener = openHost;
	return new Promise((resolve) => opener(resolve));
}

export const SendForReviewConfirmHost = () => {
	const router = useRouter();
	/* Arabic and Kannada beside English, as the screens around this one write them (router.locale). */
	const tr = (en: string, ar: string, kn: string): string =>
		router.locale === "ar-AE" ? ar : router.locale === "kn-IN" ? kn : en;

	const [pending, setPending] = React.useState<((confirmed: boolean) => void) | null>(null);

	React.useEffect(() => {
		openHost = (resolve) => setPending(() => resolve);
		return () => {
			openHost = null;
		};
	}, []);

	const answer = (confirmed: boolean) => {
		pending?.(confirmed);
		setPending(null);
	};

	return (
		<Dialog
			open={Boolean(pending)}
			onClose={() => answer(false)}
			PaperProps={{ sx: { borderRadius: "1rem", maxWidth: "24rem" } }}
		>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
				<Typography variant="h6">{tr("Send for review?", "إرسال للمراجعة؟", "ಪರಿಶೀಲನೆಗೆ ಕಳುಹಿಸುವುದೇ?")}</Typography>
				<Typography sx={{ fontSize: "0.875rem", color: "#424242" }}>
					{tr(
						"Saving these changes sends the property back for review. It stays unlisted until it's approved, which usually takes under 24 hours.",
						"حفظ هذه التغييرات يعيد العقار إلى المراجعة. يظل غير معروض حتى تتم الموافقة عليه، وعادةً ما يستغرق ذلك أقل من 24 ساعة.",
						"ಈ ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿದರೆ ಆಸ್ತಿ ಮತ್ತೆ ಪರಿಶೀಲನೆಗೆ ಹೋಗುತ್ತದೆ. ಅನುಮೋದನೆ ಸಿಗುವವರೆಗೆ ಅದು ಪಟ್ಟಿಯಲ್ಲಿ ಕಾಣಿಸುವುದಿಲ್ಲ, ಸಾಮಾನ್ಯವಾಗಿ 24 ಗಂಟೆಗಳೊಳಗೆ.",
					)}
				</Typography>
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
					<BeegruButton
						variant="outlined"
						flavor="primary"
						size="small"
						onClick={() => answer(false)}
					>
						{tr("Keep editing", "متابعة التعديل", "ಸಂಪಾದನೆ ಮುಂದುವರಿಸಿ")}
					</BeegruButton>
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						onClick={() => answer(true)}
					>
						{tr("Save changes", "حفظ التغييرات", "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ")}
					</BeegruButton>
				</div>
			</DialogContent>
		</Dialog>
	);
};
